// ============================================================
//  src/admin/pages/AdminWidgetsPage.js — Bot-Father → Widgets
//  Monthly price for the Website Widget "Remove branding" add-on,
//  plus who is paying and the revenue it brings in.
// ============================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getWidgetBranding, saveWidgetBranding } from '../api/adminClient';
import { PageHeader, StatCard, Badge } from '../components/UI';
import DataTable from '../components/DataTable';

const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const day = s => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function AdminWidgetsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [price, setPrice] = useState('');
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => getWidgetBranding().then(res => {
    setData(res.data);
    setPrice(String(res.data.price ?? ''));
    setLink(res.data.link || '');
  }).catch(e => { toast.error(e?.response?.data?.message || 'Could not load'); setData({ stats: {}, list: [] }); });
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await saveWidgetBranding({ price: Number(price), link: link.trim() });
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Save failed'); }
    setSaving(false);
  }

  if (!data) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading…</div>;
  const s = data.stats || {};

  const columns = [
    { key: 'label', label: 'Widget', primary: true, render: r => <span><b>{r.label}</b>{r.domains?.[0] ? <span style={{ color: 'var(--bf-muted, #6b7280)' }}> · {r.domains[0]}</span> : null}</span> },
    { key: 'owner', label: 'Owner', render: r => <span>{r.owner || '—'}<br /><small style={{ color: 'var(--bf-muted, #6b7280)' }}>{r.email}</small></span> },
    { key: 'until', label: 'Paid until', render: r => day(r.until) },
    { key: 'status', label: 'Status', render: r => (r.active ? <Badge tone="success">Removed</Badge> : <Badge tone="muted">Lapsed</Badge>) },
    { key: 'autoRenew', label: 'Auto-renew', render: r => (r.autoRenew ? 'On' : 'Off') },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Website Widget — Remove branding"
        subtitle="Owners pay this monthly from their CommandKit wallet to hide “Powered by CommandKit” on one widget. Auto-renew runs with the wallet cron." />

      <div className="bf-stat-grid">
        <StatCard label="Paying widgets" value={s.active ?? 0} sub={`${s.autoRenew ?? 0} on auto-renew`} accent="var(--bf-success)" />
        <StatCard label="Revenue · last 30 days" value={naira(s.revenue30d)} />
        <StatCard label="Revenue · all time" value={naira(s.revenueAll)} />
        <StatCard label="All widgets" value={s.widgets ?? 0} sub={`${s.lapsed ?? 0} lapsed`} />
      </div>

      <form onSubmit={save} className="bf-card" style={{ maxWidth: 640 }}>
        <div className="bf-card-hdr">Pricing</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bf-field">
            <label className="bf-field-label">Monthly price (₦) per widget</label>
            <input className="bf-input" type="number" min="100" step="50" value={price} onChange={e => setPrice(e.target.value)} required />
            <div className="bf-field-hint">Applies to new purchases and the next renewals. Already-paid months aren’t changed.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">“Powered by CommandKit” link</label>
            <input className="bf-input" value={link} onChange={e => setLink(e.target.value)} placeholder="https://cmdkit.im/?ref=widget" />
            <div className="bf-field-hint">Where visitors go when they tap “Powered by CommandKit” on a free widget — your sign-up page works well.</div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      <div className="bf-card">
        <div className="bf-card-hdr">Widgets that paid</div>
        <DataTable columns={columns} rows={data.list || []} keyField="wId"
          onRowClick={r => r.ownerId && navigate(`/bot-father/users/${r.ownerId}`)}
          emptyMessage="No one has removed branding yet." />
      </div>
    </div>
  );
}
